import { useEffect, useState } from "react";
import { AlertCircle, Check, ExternalLink, Loader2, Plug } from "lucide-react";
import {
  connectGoldsmith,
  disconnectGoldsmith,
  getGoldsmithStatus,
  type GoldsmithStatus,
} from "../../features/goldsmith/goldsmithApi";

const FEATURE_LABEL: Record<string, string> = {
  photoshoot: "AI Photoshoot",
  video: "AI Short Video",
  production: "Jewelry Fixer",
};

/**
 * Settings → Integrations → TheGoldsmith.
 *
 * The key is checked against Goldsmith BEFORE it is saved (a stored-but-dead
 * key would only fail at the moment someone sends a ring), stored encrypted per
 * workspace, and never shown again — only its id and last four characters.
 */
export default function IntegrationsSettings() {
  const [status, setStatus] = useState<GoldsmithStatus | null>(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getGoldsmithStatus().then(setStatus).catch((e: Error) => setError(e.message));
  }, []);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      setStatus(await connectGoldsmith(key.trim()));
      setKey("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not connect");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect TheGoldsmith? Results already saved to your Vault stay there.")) return;
    setBusy(true);
    try {
      setStatus(await disconnectGoldsmith());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface-card mt-6 p-6" data-goldsmith-settings>
      <div className="flex items-center gap-2">
        <Plug size={16} className="text-gold-500" />
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink-400">TheGoldsmith AI</h2>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-ink-500">
        Send a finished ring from the Studio or the Ring Builder to TheGoldsmith for an <b>AI Photoshoot</b> or an{" "}
        <b>AI Short Video</b>. Results are saved to your Vault. Jobs use your TheGoldsmith credits.
      </p>

      {!status && !error ? (
        <div className="mt-4 flex items-center gap-2 text-[13px] text-ink-500">
          <Loader2 size={14} className="animate-spin" /> Checking…
        </div>
      ) : status?.connected ? (
        <div className="mt-4">
          <div
            className={`flex items-start gap-2 rounded-xl border px-4 py-3 ${
              status.error ? "border-rose/30 bg-rose/5" : "border-emerald/30 bg-emerald/5"
            }`}
          >
            {status.error ? <AlertCircle size={16} className="mt-0.5 text-rose" /> : <Check size={16} className="mt-0.5 text-emerald" />}
            <div className="text-[13px]">
              <p className="font-semibold text-ink-900">{status.error ? "Connected, but TheGoldsmith is not answering" : `Connected${status.name ? ` — ${status.name}` : ""}`}</p>
              <p className="text-ink-500">
                Key <span className="font-mono">{status.keyPreview}</span>
                {status.features?.length ? <> · {status.features.filter((f) => FEATURE_LABEL[f]).map((f) => FEATURE_LABEL[f]).join(", ")}</> : null}
              </p>
              {status.error ? <p className="mt-1 text-rose">{status.error}</p> : null}
              {!status.error && typeof status.creditsUsedToday === "number" ? (
                <p className="mt-1 text-ink-500">
                  {status.creditsUsedToday} credits used today
                  {status.creditsRemainingToday != null ? ` · ${status.creditsRemainingToday} left on this key's daily cap` : ""}
                </p>
              ) : null}
            </div>
          </div>
          {status.features && !status.features.includes("production") ? (
            <p className="mt-2 text-[12px] text-ink-400">
              Jewelry Fixer switches on when your TheGoldsmith key includes it (and TheGoldsmith offers it to partner keys).
            </p>
          ) : null}
          <button
            onClick={disconnect}
            disabled={busy}
            className="mt-3 rounded-lg border border-ink-200 px-3 py-1.5 text-[12.5px] font-medium text-ink-600 hover:border-rose hover:text-rose disabled:opacity-50"
          >
            Disconnect
          </button>
        </div>
      ) : (
        <>
          <ol className="mt-4 list-decimal space-y-1 pl-5 text-[12.5px] text-ink-500">
            <li>
              In TheGoldsmith, open <b>Settings → API Keys</b> and create a key with <b>Photoshoot</b> and <b>Video</b>.
            </li>
            <li>Copy it (it starts with <span className="font-mono">gsk_</span>) and paste it below.</li>
          </ol>
          {error ? (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose/30 bg-rose/5 px-3 py-2.5 text-[12.5px] text-rose">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}
          <div className="mt-4 flex gap-2">
            <input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="gsk_…"
              className="min-w-0 flex-1 rounded-lg border border-ink-200 px-3 py-2 font-mono text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
              data-goldsmith-key
            />
            <button
              onClick={() => void connect()}
              disabled={busy || key.trim().length < 10}
              className="flex items-center gap-1.5 rounded-lg gold-gradient px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
              data-goldsmith-connect
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              Connect
            </button>
          </div>
          {status?.origin ? (
            <a href={status.origin} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-[12px] text-ink-400 hover:text-ink-600">
              Open TheGoldsmith <ExternalLink size={11} />
            </a>
          ) : null}
        </>
      )}
    </div>
  );
}
